import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useWallet } from "@/context/WalletContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bitcoin, Sparkles, RefreshCw, Save, Trash2 } from "lucide-react";
import AdminTabsNav from "@/components/AdminTabsNav";
import Header from "@/components/Header";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtUsd(v) {
  if (v === null || v === undefined) return "—";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export default function AdminBondTracker() {
  const { getAuthHeaders } = useWallet();
  const [triad, setTriad] = useState(null);
  const [current, setCurrent] = useState(null);
  const [btcQty, setBtcQty] = useState("");
  const [autoPrefer, setAutoPrefer] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [tRes, qRes, apRes] = await Promise.all([
        fetch(`${API}/api/tvl/triad`),
        fetch(`${API}/api/admin/bond-tvl/btc-qty`, { headers }),
        fetch(`${API}/api/admin/bond-tvl/auto-prefer`, { headers }),
      ]);
      if (tRes.ok) setTriad((await tRes.json())?.metrics || null);
      if (qRes.ok) {
        const q = await qRes.json();
        setCurrent(q);
        if (q?.btc_qty !== null && q?.btc_qty !== undefined) setBtcQty(String(q.btc_qty));
      }
      if (apRes.ok) {
        const ap = await apRes.json();
        setAutoPrefer(ap?.enabled !== false);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const val = parseFloat(btcQty);
    if (Number.isNaN(val) || val < 0) {
      toast.error("Enter a valid BTC quantity (0 or more)");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/bond-tvl/btc-qty`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ btc_qty: val }),
      });
      const data = await res.json();
      if (res.ok && data?.success) {
        toast.success(`Set ${val} BTC → Bond TVL ${fmtUsd(data.bond_tvl_usd_preview)} (BTC $${data.btc_price_usd?.toLocaleString?.() ?? "?"})`);
        load();
      } else {
        toast.error(data?.detail || "Failed to set BTC quantity");
      }
    } catch (e) {
      toast.error("Request failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear the enrolled BTC quantity? The Bond Tracker will fall back to DeFiLlama auto-discovery (likely $0 until indexed).")) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/bond-tvl/btc-qty`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ btc_qty: null }),
      });
      if (res.ok) {
        toast.success("Cleared");
        setBtcQty("");
        load();
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleAutoPrefer = async (next) => {
    setAutoPrefer(next);
    try {
      const res = await fetch(`${API}/api/admin/bond-tvl/auto-prefer`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      if (res.ok) {
        toast.success(next ? "Auto-prefer DeFiLlama ON — live feed takes over once indexed" : "Auto-prefer OFF — manual quantity is the source of truth");
        load();
      } else {
        setAutoPrefer(!next);
        toast.error("Failed to update toggle");
      }
    } catch {
      setAutoPrefer(!next);
      toast.error("Request failed");
    }
  };

  const activeSource = triad?.sources?.bond_tvl_usd || "—";
  const onDefillama = String(activeSource).startsWith("defillama:");

  return (
    <div className="min-h-screen bg-background">
    <Header />
    <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="admin-bond-tracker-page">
      <AdminTabsNav active="bonds" />

      <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
        <Bitcoin className="w-6 h-6 text-orange-400" /> Bond Tracker
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        Set the enrolled BTC quantity. Bond TVL = BTC qty × live BTC price (auto-updates),
        STX 5% Matched = 5% of that. Bump the quantity as more bonds lock.
      </p>

      {/* Live preview */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Card className="glass-card border-purple-500/30">
          <CardContent className="p-4">
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 mb-1">
              <Bitcoin className="w-3.5 h-3.5 text-purple-400" /> Bitcoin Protocol Bonds
            </div>
            <div className="font-mono text-xl font-bold" data-testid="bond-preview-usd">{fmtUsd(triad?.bond_tvl_usd)}</div>
            <div className="font-mono text-[11px] text-muted-foreground/80 mt-0.5">
              {triad?.bond_btc_qty ? `${triad.bond_btc_qty} BTC enrolled` : "no quantity set"}
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-orange-500/30">
          <CardContent className="p-4">
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-orange-300" /> STX 5% Matched
            </div>
            <div className="font-mono text-xl font-bold" data-testid="stx-preview-usd">{fmtUsd(triad?.stxoshi_locked_usd)}</div>
            <div className="font-mono text-[11px] text-muted-foreground/80 mt-0.5">
              {triad?.stx_matched_qty ? `≈ ${Number(triad.stx_matched_qty).toLocaleString(undefined, { maximumFractionDigits: 0 })} STX` : "—"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data source toggle */}
      <Card className="glass-card mb-4">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-medium text-sm">Auto-prefer DeFiLlama once live</div>
              <p className="text-[11px] text-muted-foreground mt-1">
                When ON, the tracker automatically switches to DeFiLlama's live protocol TVL
                the moment it's indexed — your BTC quantity is used only as a pre-launch placeholder.
                When OFF, your manual number stays the source of truth.
              </p>
            </div>
            <Switch checked={autoPrefer} onCheckedChange={toggleAutoPrefer} data-testid="bond-auto-prefer-toggle" />
          </div>
          <div className="mt-3 text-[11px] font-mono flex items-center gap-2">
            <span className="text-muted-foreground">Active source:</span>
            <span className={onDefillama ? "text-green-400" : "text-orange-300"} data-testid="bond-active-source">
              {onDefillama ? `DeFiLlama (${activeSource})` : `Manual (${activeSource})`}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Enrolled BTC Quantity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="btc-qty">BTC enrolled</Label>
            <Input
              id="btc-qty"
              type="number"
              min="0"
              step="0.00000001"
              value={btcQty}
              onChange={(e) => setBtcQty(e.target.value)}
              placeholder="e.g. 230"
              className="mt-1 font-mono"
              data-testid="bond-btc-qty-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              {current?.updated_at ? `Last set ${new Date(current.updated_at).toLocaleString()}` : "Not set yet"}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="flex-1" data-testid="bond-btc-qty-save">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Set Quantity
            </Button>
            <Button onClick={clear} disabled={saving || loading} variant="outline" className="border-border text-muted-foreground" data-testid="bond-btc-qty-clear">
              <Trash2 className="w-4 h-4 mr-2" /> Clear
            </Button>
            <Button onClick={load} disabled={loading} variant="outline" size="icon" data-testid="bond-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
    </div>
  );
}
